import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import type {
  PermissionGroup,
  PermissionGroupAssignedUserDetailsParams,
  PermissionGroupDetailsResponse,
} from 'src/apis/authorization-service/types';
import { usePermissionGroupAssignedUsers } from 'src/apis/authorization-service/use-permission-group-assigned-users';
import { useUnassignAllUsersFromPermissionGroup } from 'src/apis/authorization-service/use-unassign-all-users-from-permission-group';

import { Stack } from 'src/components/layout/stack';
import { Modal } from 'src/components/overlay/modal';
import type { SavedReferrer } from 'src/hooks/use-referrer';
import { useToast } from 'src/hooks/use-toasts';
import { UserTableWrapper } from 'src/pages/settings/permission-groups/overview/user-list';

type Props = {
  close: VoidFunction;
  onSuccess: VoidFunction;
  unassignAllUsersUrl: string;
  permissionGroup: PermissionGroup | PermissionGroupDetailsResponse;
  savedReferrer: SavedReferrer;
};

export const UnassignAllUsersPermissionGroupModal: FC<Props> = ({
  close,
  onSuccess,
  unassignAllUsersUrl: unassignAllUsers,
  permissionGroup,
  savedReferrer,
}) => {
  const notify = useToast();
  const unassignAllUsersFromGroup = useUnassignAllUsersFromPermissionGroup();

  const assignedUsersParams = useMemo(
    (): PermissionGroupAssignedUserDetailsParams => ({
      limit: 500,
      offset: 0,
    }),
    []
  );
  const assignedUsers = usePermissionGroupAssignedUsers({
    assignedUsersLink: permissionGroup._links.assignedUsers,
    params: assignedUsersParams,
  });

  useEffect(() => {
    if (!unassignAllUsersFromGroup.response) return;

    const usersWithErrors = unassignAllUsersFromGroup.response.data.results
      .filter((el) => !el.success)
      .map((el) => el.errorMessage);
    if (usersWithErrors.length > 0) {
      notify({
        type: 'error',
        children: (
          <div data-testid="unassign-all-users-permission-group-error-toast">
            Some users have been successfully unassigned, but there
            {usersWithErrors.length > 1
              ? ' were errors for others:'
              : ' was an error for one user:'}
            {usersWithErrors.map((userError, index) => (
              <li key={index}>{userError}</li>
            ))}
          </div>
        ),
      });
      if (permissionGroup._links.assignedUsers) {
        assignedUsers?.execute({
          assignedUsersLink: permissionGroup._links.assignedUsers,
          params: assignedUsersParams,
        });
      }
      return;
    }
    notify({
      type: 'success',
      children: 'All users have been successfully unassigned.',
    });
    onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unassignAllUsersFromGroup.response]);

  const usersCount = permissionGroup.assignedUserCount;

  return (
    <Modal
      data-testid="unassign-all-users-permission-group-modal"
      title="Unassign all users from permission group"
      onDismiss={close}
      footer={{
        confirmButton: {
          label: unassignAllUsersFromGroup.pending
            ? 'Unassigning...'
            : 'Unassign all users',
          onClick: () => unassignAllUsersFromGroup.execute(unassignAllUsers),
          dataTestId: 'unassign-all-users-permission-group-submit',
        },
      }}
    >
      <Stack gap={1.5}>
        <p>
          This group has become obsolete. These permissions don't apply to you
          anymore, because no relevant service is subscribed to by you or one of
          your operators. Please unassign all users from this group to remove
          it.
        </p>

        <p data-testid="unassign-all-users-permission-group-modal-user-info">
          The following {usersCount > 1 ? 'users' : 'user'} will be unassigned
          from this group:
        </p>
        <UserTableWrapper
          savedReferrer={savedReferrer}
          assignedUsers={assignedUsers}
        />
      </Stack>
    </Modal>
  );
};
